/**
 * useSiteLayer Hook
 *
 * Creates and manages a DeckGL IconLayer for rendering site markers with GPU acceleration.
 * This hook encapsulates the IconLayer creation with proper memoization to prevent
 * unnecessary layer recreation on each render.
 *
 * Key features:
 * - GPU-accelerated rendering via DeckGL IconLayer
 * - Picking support for hover/click interactions
 * - Proper memoization for performance
 */

import { PickingInfo } from '@deck.gl/core';
import { IconLayer } from '@deck.gl/layers';
import { useCallback, useMemo } from 'react';

import {
  ACTIVE_SITE_ICON_SIZE,
  DEFAULT_SITE_ICON_SIZE,
  SITE_ICON_ATLAS,
  SITE_ICON_MAPPING,
  SiteIconType,
} from '~components/Maps/v2/siteIconAtlas';
import { SiteLayerDatum } from '~components/Maps/v2/utils/transformSiteData';

/**
 * Information returned when a site is picked (hovered or clicked).
 */
export interface SitePickInfo {
  /** The site data that was picked */
  site: SiteLayerDatum;
  /** Screen X coordinate of the pick event */
  x: number;
  /** Screen Y coordinate of the pick event */
  y: number;
}

/**
 * Options for configuring the site layer behavior.
 */
interface UseSiteLayerOptions {
  /** Callback when a site is hovered */
  onHover?: (info: SitePickInfo | null) => void;
  /** Callback when a site is clicked */
  onClick?: (info: SitePickInfo) => void;
  /** ID of the currently active/selected marker */
  activeMarkerId?: string | null;
  /** Base size for site icons in pixels */
  iconSize?: number;
}

/**
 * Return type for the useSiteLayer hook.
 */
interface UseSiteLayerResult {
  /** The site icon layer for DeckGL */
  siteIconLayer: IconLayer<SiteLayerDatum> | null;
  /** Array containing the site icon layer for convenience */
  layers: IconLayer<SiteLayerDatum>[];
}

/**
 * Default icon size for site markers.
 */
const DEFAULT_ICON_SIZE = DEFAULT_SITE_ICON_SIZE;

/**
 * Creates a DeckGL IconLayer for rendering site markers.
 *
 * This hook creates an IconLayer that:
 * - Renders site icons from the icon atlas
 * - Supports active state with larger icon size
 * - Enables picking for hover/click interactions
 *
 * @param siteData - Array of transformed site data
 * @param options - Configuration options for layer behavior
 * @returns Object containing the created layer
 *
 * @example
 * ```tsx
 * const { layers } = useSiteLayer(siteData, {
 *   onHover: (info) => setHoveredSite(info?.site ?? null),
 *   onClick: (info) => setSelectedSite(info.site),
 * });
 *
 * // Pass layers to MapV2's overlayLayers prop
 * <MapV2 overlayLayers={[...geofenceLayers, ...layers, ...truckLayers]} />
 * ```
 */
export const useSiteLayer = (
  siteData: SiteLayerDatum[],
  options: UseSiteLayerOptions = {},
): UseSiteLayerResult => {
  const { onHover, onClick, activeMarkerId, iconSize = DEFAULT_ICON_SIZE } = options;

  /**
   * Handler for DeckGL hover events.
   * Transforms the PickingInfo into our SitePickInfo format.
   */
  const handleHover = useCallback(
    (info: PickingInfo<SiteLayerDatum>) => {
      if (!onHover) return;

      if (info.object) {
        onHover({
          site: info.object,
          x: info.x,
          y: info.y,
        });
      } else {
        onHover(null);
      }
    },
    [onHover],
  );

  /**
   * Handler for DeckGL click events.
   * Only fires callback if an object was actually clicked.
   */
  const handleClick = useCallback(
    (info: PickingInfo<SiteLayerDatum>) => {
      if (!onClick) return;

      if (info.object) {
        onClick({
          site: info.object,
          x: info.x,
          y: info.y,
        });
      }
    },
    [onClick],
  );

  /**
   * Creates the site icon layer with proper memoization.
   *
   * Key features:
   * - Uses icon atlas for efficient texture-based rendering
   * - Supports active state with larger icon size
   * - Enables picking for hover/click interactions
   */
  const siteIconLayer = useMemo(() => {
    if (siteData.length === 0) {
      return null;
    }

    return new IconLayer<SiteLayerDatum>({
      id: 'site-icon-layer',
      data: siteData,

      // Icon atlas configuration
      iconAtlas: SITE_ICON_ATLAS,
      iconMapping: SITE_ICON_MAPPING,

      // Position and icon accessors
      getPosition: (d: SiteLayerDatum) => d.position,
      getIcon: (d: SiteLayerDatum) => d.icon as SiteIconType,
      getSize: (d: SiteLayerDatum) =>
        d.id === activeMarkerId ? ACTIVE_SITE_ICON_SIZE : iconSize,

      // Rendering options
      sizeUnits: 'pixels',
      sizeScale: 1,
      sizeMinPixels: 16,
      sizeMaxPixels: 48,
      billboard: true, // Always face the camera for readability

      // Interaction settings
      pickable: true,
      onHover: handleHover,
      onClick: handleClick,

      // Performance settings
      updateTriggers: {
        getSize: activeMarkerId, // Re-evaluate size when active marker changes
        getIcon: activeMarkerId, // Re-evaluate icon when active marker changes
      },
    });
  }, [siteData, activeMarkerId, iconSize, handleHover, handleClick]);

  /**
   * Combined layers array for easy consumption.
   * Filters out null layers and returns a flat array.
   */
  const layers = useMemo(() => {
    const result: IconLayer<SiteLayerDatum>[] = [];

    if (siteIconLayer) {
      result.push(siteIconLayer);
    }

    return result;
  }, [siteIconLayer]);

  return {
    siteIconLayer,
    layers,
  };
};
